import { createFileRoute } from '@tanstack/react-router';
import { useRef, useMemo, lazy, Suspense } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { AuctionIcon, ArrowRightIcon, ArrowLeftIcon, MenuIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { useCvAssessmentDetailsQuery } from '@/app/hooks/queries/useCvAssessment';
import { Tabs, TabsList, TabsTab, TabsPanel } from '@/app/ui/components/tabs';
import { Button } from '@/app/ui/components/button';
import { CvPreviewRef, mapStatementsToType } from '@/app/components/features/bids/BidCvs/utils';
import {
  Sidenav,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavLink,
  SidenavList,
} from '@/app/ui/components/sidenav';
import { useTranslations } from 'use-intl';
import { CvAssessmentPositionStatement } from '@/app/types/models';
import CitationListItem from '@/app/components/shared/Citations/CitationListItem';
import { CvAssessmentStatementType } from '@/app/utils/constants';
import FeedbackCard from '@/app/components/shared/Feedback/FeedbackCard';
import { useSetCvAssessmentStatementFeedbackMutation } from '@/app/hooks/queries/useCvAssessment';
import ReactMarkdown from 'react-markdown';
import { ScrollArea } from '@/app/ui/components/scroll-area';
import { CircularProgress } from '@/app/ui/components/circular-progress';
import { BadgeDot } from '@/app/ui/components/badge';
import { atom, useAtom, useSetAtom } from 'jotai';
import { Sheet, SheetPanel, SheetPopup, SheetTrigger } from '@/app/ui/components/sheet';
import { getBidAssessmentColor } from '@/app/components/features/bids/utils';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSuspenseQuery } from '@tanstack/react-query';

const CvPreview = lazy(() => import('@/app/components/features/bids/BidCvs/CvPreview'));
enum TabsValue {
  cv = 'cv',
  summary = 'summary',
}

const queryAtom = atom<string | undefined>(undefined);
const queryTypeAtom = atom<CvAssessmentStatementType | undefined>(undefined);
const activeIndexAtom = atom<number | undefined>(undefined);
const activeTypeAtom = atom<CvAssessmentStatementType | undefined>(undefined);
const tabAtom = atom<TabsValue>(TabsValue.cv);

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/cv-analysis/$positionId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tCv = useTranslations('components.bidCvs');
  const tCvDetails = useTranslations('components.bidCvs.cvDetails');
  const { bidId, tenderPackageId, projectId, positionId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const [tab, setTab] = useAtom(tabAtom);
  const [query, setQuery] = useAtom(queryAtom);
  const [queryType, setQueryType] = useAtom(queryTypeAtom);
  const setActiveIndex = useSetAtom(activeIndexAtom);
  const setActiveType = useSetAtom(activeTypeAtom);
  const cvPreviewRef = useRef<CvPreviewRef>(null);

  const { data } = useCvAssessmentDetailsQuery({
    bidId,
  });

  const { selectedPosition, categoryPositions } = useMemo(() => {
    if (!data) return { selectedPosition: null, categoryPositions: [] };

    let selectedPosition = null;
    let category = null;

    for (const cat of data.categories) {
      const pos = cat.positions.find((p) => p.id === positionId);
      if (pos) {
        selectedPosition = pos;
        category = cat;
        break;
      }
    }

    const positions = category?.positions ?? [];
    return { selectedPosition, categoryPositions: positions };
  }, [data, positionId]);

  const handleTabChange = (value: string) => {
    setTab(value as TabsValue);

    if (value === TabsValue.summary) {
      setActiveIndex(undefined);
      setActiveType(undefined);
      setQuery(undefined);
      setQueryType(undefined);
    }
  };

  if (!selectedPosition) return null;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden @xl:inline-flex" />
            <BreadcrumbItem className="hidden @xl:inline-flex">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/cv-analysis"
                params={{ projectId, tenderPackageId, bidId }}
              >
                {tCv('cards.cvAnalysis.title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tCv('cards.cvAnalysis.cvDetails')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Sheet>
          <SheetTrigger
            render={
              <HeaderButton className="md:flex @5xl:hidden!">
                <HugeiconsIcon icon={MenuIcon} />
              </HeaderButton>
            }
          />
          <SheetPopup side="bottom" className={'h-[90svh]'}>
            <SheetPanel className="p-0!">
              <Assessment cvPreviewRef={cvPreviewRef} />
            </SheetPanel>
          </SheetPopup>
        </Sheet>
        <HeaderSidechatButton />
      </Header>
      <Tabs
        className="flex grow min-h-0 @5xl:flex-row min-w-0 flex-col"
        value={tab}
        onValueChange={handleTabChange}
      >
        <div className="flex grow @6xl:flex-row flex-col min-w-0">
          <Sidenav className="border-r xl:w-auto lg:w-auto @7xl:w-68 @6xl:w-56">
            <SidenavHeader className="max-lg:hidden">
              <SidenavHeading> {tCv('labels.positions')}</SidenavHeading>
            </SidenavHeader>
            <SidenavList className="lg:flex-row @6xl:flex-col">
              {categoryPositions.map((position) => (
                <SidenavItem key={position.id}>
                  <SidenavLink
                    to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/cv-analysis/$positionId"
                    params={{ projectId, tenderPackageId, bidId, positionId: position.id }}
                    onClick={() => {
                      setActiveIndex(undefined);
                      setActiveType(undefined);
                    }}
                  >
                    <span className="@6xl:line-clamp-1">{position.positionName}</span>
                  </SidenavLink>
                </SidenavItem>
              ))}
            </SidenavList>
          </Sidenav>
          <div className="flex flex-col grow min-h-0 max-md:pb-[calc(var(--bottom-navigation-height))]">
            <TabsList
              className={'min-h-[calc(var(--spacing)*11-var(--spacing)*0.25)] flex items-end px-3'}
            >
              <TabsTab value={TabsValue.cv}>{tCvDetails('originalCv')}</TabsTab>
              <TabsTab value={TabsValue.summary}>{tCvDetails('summary')}</TabsTab>
            </TabsList>
            <TabsPanel value={TabsValue.cv}>
              <Suspense fallback={null}>
                <CvPreview
                  ref={cvPreviewRef}
                  documentId={selectedPosition.bidDocId}
                  query={query}
                  queryType={queryType}
                />
              </Suspense>
            </TabsPanel>
            <TabsPanel value={TabsValue.summary} render={<ScrollArea viewportClassName={'p-4'} />}>
              <div className="flex flex-col">
                <div>
                  <p className="text-sm text-muted">{selectedPosition.positionName}</p>
                  <p className="text-2xl font-semibold mb-4">{tCv('cvSummary.title')}</p>
                </div>
                {selectedPosition.candidateSummary && (
                  <ReactMarkdown className="prose">
                    {selectedPosition.candidateSummary}
                  </ReactMarkdown>
                )}
              </div>
            </TabsPanel>
          </div>
        </div>
        <ScrollArea
          data-assessment
          className={'hidden @5xl:block max-w-80 shrink-0 @6xl:max-w-96 border-l'}
        >
          <Assessment cvPreviewRef={cvPreviewRef} />
        </ScrollArea>
      </Tabs>
    </>
  );
}

function Assessment({ cvPreviewRef }: { cvPreviewRef: any }) {
  const tCv = useTranslations('components.bidCvs');
  const { bidId, tenderPackageId, positionId } = Route.useParams();
  const { data } = useCvAssessmentDetailsQuery({
    bidId,
  });

  const setQuery = useSetAtom(queryAtom);
  const setQueryType = useSetAtom(queryTypeAtom);
  const [activeIndex, setActiveIndex] = useAtom(activeIndexAtom);
  const [activeType, setActiveType] = useAtom(activeTypeAtom);
  const [tab, setTab] = useAtom(tabAtom);

  const { selectedPosition } = useMemo(() => {
    if (!data) return { selectedPosition: null, categoryPositions: [] };

    let selectedPosition = null;
    let category = null;

    for (const cat of data.categories) {
      const pos = cat.positions.find((p) => p.id === positionId);
      if (pos) {
        selectedPosition = pos;
        category = cat;
        break;
      }
    }

    const positions = category?.positions ?? [];
    return { selectedPosition, categoryPositions: positions };
  }, [data, positionId]);

  const groupedData = useMemo(
    () => (selectedPosition?.statements ? mapStatementsToType(selectedPosition.statements) : null),
    [selectedPosition?.statements],
  );

  const dataOrderedByGroup = useMemo(() => {
    if (!groupedData) return [];
    return [...groupedData.strengths, ...groupedData.risks, ...groupedData.suggestions];
  }, [groupedData]);

  const handlePointClick = (query?: string, type?: CvAssessmentStatementType) => {
    setQuery(query);
    setQueryType(type);

    if (tab === TabsValue.summary) {
      setTab(TabsValue.cv);
      setTimeout(() => {
        cvPreviewRef.current?.reapplyHighlights();
      }, 100);
    }
  };

  const handleAssessmentPointClick = (
    index: number,
    type: CvAssessmentStatementType,
    bidSource: string,
  ) => {
    setActiveIndex(index);
    setActiveType(type);
    handlePointClick(bidSource, type);
  };

  const closeDetailsBar = () => {
    setActiveIndex(undefined);
    setActiveType(undefined);
    handlePointClick();
  };

  const handleStepChange = (step: number) => {
    setActiveIndex(step);
    setActiveType(dataOrderedByGroup[step].type);
    handlePointClick(dataOrderedByGroup[step].bidSource, dataOrderedByGroup[step].type);
  };

  return (
    <>
      {activeIndex !== undefined && activeType && groupedData ? (
        <CvAssessmentPointSidebar
          data={dataOrderedByGroup}
          currentStep={activeIndex}
          onStepChange={handleStepChange}
          onBack={closeDetailsBar}
          activeType={activeType}
        />
      ) : selectedPosition ? (
        <>
          <div className="p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-muted uppercase">
                {tCv('assessmentPointSidebar.labels.cvAssessment')}
              </p>
              <p className="text-xl font-semibold">
                {selectedPosition.candidateName || tCv('labels.anonymous')}
              </p>
              <p className="text-sm text-muted">{selectedPosition.positionName}</p>
            </div>
            {selectedPosition.score ? (
              <CircularProgress
                max={10}
                getValueText={(value) => value.toFixed(1)}
                value={selectedPosition.score}
                size={75}
                color={getBidAssessmentColor(selectedPosition.score)}
              />
            ) : null}
          </div>
          <div>
            {groupedData?.strengths && groupedData.strengths.length > 0 && (
              <>
                <p className="font-medium px-4 py-2">{tCv('labels.strengths')}</p>
                <div className="divide-y">
                  {groupedData.strengths.map((item) => (
                    <div
                      key={item.id}
                      tabIndex={0}
                      className="flex gap-3 px-4 py-2 cursor-pointer group hover:bg-surface-2"
                      onClick={() =>
                        handleAssessmentPointClick(
                          item.originalIndex,
                          CvAssessmentStatementType.STRENGTH,
                          item.bidSource,
                        )
                      }
                    >
                      <BadgeDot variant="success" className="mt-1.5" />
                      <p className="text-sm">{item.statement}</p>
                      <HugeiconsIcon icon={ArrowRightIcon} className="size-5 ml-2 shrink-0" />
                    </div>
                  ))}
                </div>
              </>
            )}
            {groupedData?.risks && groupedData.risks.length > 0 && (
              <>
                <p className="font-medium px-4 py-2">{tCv('labels.risks')}</p>
                <div className="divide-y">
                  {groupedData.risks.map((item) => (
                    <div
                      key={item.id}
                      tabIndex={0}
                      className="flex gap-3 px-4 py-2 cursor-pointer group hover:bg-surface-2"
                      onClick={() =>
                        handleAssessmentPointClick(
                          item.originalIndex,
                          CvAssessmentStatementType.RISK,
                          item.bidSource,
                        )
                      }
                    >
                      <BadgeDot variant="destructive" className="mt-1.5" />
                      <p className="flex-1 text-sm">{item.statement}</p>
                      <HugeiconsIcon icon={ArrowRightIcon} className="size-5 ml-2 shrink-0" />
                    </div>
                  ))}
                </div>
              </>
            )}
            {groupedData?.suggestions && groupedData.suggestions.length > 0 && (
              <>
                <p className="font-medium px-4 py-2">{tCv('labels.improvementSuggestions')}</p>
                <div className="divide-y">
                  {groupedData.suggestions.map((item) => (
                    <div
                      key={item.id}
                      tabIndex={0}
                      className="flex gap-3 px-4 py-2 cursor-pointer group hover:bg-surface-2"
                      onClick={() =>
                        handleAssessmentPointClick(
                          item.originalIndex,
                          CvAssessmentStatementType.SUGGESTION,
                          item.bidSource,
                        )
                      }
                    >
                      <BadgeDot variant="warning" className="mt-1.5" />
                      <p className="flex-1 text-sm">{item.statement}</p>
                      <HugeiconsIcon icon={ArrowRightIcon} className="size-5 ml-2 shrink-0" />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      ) : null}
    </>
  );
}

function CvAssessmentPointSidebar({
  onBack,
  data,
  currentStep,
  onStepChange,
  activeType,
}: {
  currentStep: number;
  data: CvAssessmentPositionStatement[];
  onStepChange: (step: number) => void;
  onBack: () => void;
  activeType: CvAssessmentStatementType;
}) {
  const { bidId } = Route.useParams();
  const t = useTranslations('components.bidCvs.assessmentPointSidebar');
  const allStepsCount = data.length;

  const { mutateAsync: sendFeedback, isPending: isSendingFeedback } =
    useSetCvAssessmentStatementFeedbackMutation(bidId);

  const handleStepChange = (direction: 'next' | 'prev') => {
    if (direction === 'next' && currentStep < allStepsCount - 1) {
      onStepChange(currentStep + 1);
    } else if (direction === 'prev' && currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  const handleSendFeedback = async (isPositive: boolean) => {
    await sendFeedback({
      statementId: data[currentStep].id,
      isPositiveFeedback: isPositive,
    });
  };

  const activeTypeVariantMap: Record<
    CvAssessmentStatementType,
    'success' | 'destructive' | 'warning'
  > = {
    [CvAssessmentStatementType.STRENGTH]: 'success',
    [CvAssessmentStatementType.RISK]: 'destructive',
    [CvAssessmentStatementType.SUGGESTION]: 'warning',
  };

  const item = data[currentStep];

  return (
    <>
      <div className="justify-between min-h-11 flex items-center px-2 border-b">
        <Button onClick={onBack} size="sm" variant="ghost" kind="with-icon">
          <HugeiconsIcon icon={ArrowLeftIcon} />
          {t('buttons.backToList')}
        </Button>
        <div>
          <Button
            onClick={() => handleStepChange('prev')}
            variant="ghost"
            size="sm"
            disabled={currentStep === 0}
            kind="icon"
          >
            <HugeiconsIcon icon={ArrowLeftIcon} />
          </Button>
          <Button
            onClick={() => handleStepChange('next')}
            variant="ghost"
            size="sm"
            disabled={currentStep === allStepsCount - 1}
            kind="icon"
          >
            <HugeiconsIcon icon={ArrowRightIcon} />
          </Button>
        </div>
      </div>
      <div className="flex gap-3 pb-4 py-3 px-4">
        <BadgeDot variant={activeTypeVariantMap[activeType]} className="mt-1.5" />
        <div>
          <ReactMarkdown className="text-sm font-medium">{item.statement}</ReactMarkdown>
          {!!item.rfpStatement && (
            <>
              <div>
                <p className="text-sm mt-3 font-semibold">{t('labels.rfpStatement')}</p>
                <ReactMarkdown className="text-sm pt-0.5">{item.rfpStatement}</ReactMarkdown>
              </div>
              <div>
                <p className="uppercase text-sm text-muted mt-3 py-0.5">{t('labels.source')}</p>
                <CitationListItem
                  withAccent
                  showIcon={false}
                  documentId={item?.rfpDocId}
                  content={item?.rfpSource}
                  source={item?.rfpSource}
                  title={t('labels.docNamePlaceholder')}
                />
              </div>
            </>
          )}
          {!!item.explanation && (
            <div className="px-4">
              <p className="text-sm font-semibold">{t('labels.explanation')}</p>
              <ReactMarkdown className="text-sm pt-0.5 pb-0.75">{item.explanation}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
      <div className="p-4">
        <FeedbackCard
          feedback={item.isPositiveFeedback}
          customFeedbackHandler={handleSendFeedback}
          isLoading={isSendingFeedback}
        />
      </div>
    </>
  );
}
